import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import JsonLd from "./JsonLd";
import { buildOrganizationSchema } from "../../data/seo/structuredData";
import { indiaBlogIndexNoindex } from "../../data/seo/indiaBlogSlugs";
import { marketingOrigin } from "../../data/seo/siteEntity";
import { getDocSection, TIER_META, type DocTier } from "../../content/docs/docsManifest";
import { isPmsGuidePath } from "../../content/support/pmsSupportManifest";
import { applyRobotsMeta, NOINDEX_ROBOTS } from "../../hooks/robotsMeta";

/**
 * Sitewide JSON-LD for the public marketing shell, plus `noindex` for routes
 * that must not be crawled as pages (auth, SSO, the docs PDF input).
 *
 * Organization is omitted on `/` and `/contact` because those pages already
 * emit the same `@id`. BreadcrumbList is omitted where the page builds its
 * own (use cases, comparisons, case studies, the blog index) so a route never
 * publishes two trails.
 */

const LABELS: Record<string, string> = {
    "/pricing": "Pricing",
    "/contact": "Contact",
    "/ai-chat-widget": "AI Chat Widget",
    "/ai-voice-agents": "AI Voice Agents",
    "/ai-knowledge-base": "Knowledge Base",
    "/ai-workflow-builder": "Workflow Builder",
    "/ai-call-pipeline": "Call Pipeline",
    "/ai-outbound-triggers": "Outbound Triggers",
    "/ai-crm": "AI CRM",
    "/sales-call-pipeline": "Sales Call Pipeline",
    "/managed-support": "Managed Support",
    "/shopify-ai-customer-support": "Shopify AI Support",
    "/privacy": "Privacy Policy",
    "/terms": "Terms of Service",
    "/cookies": "Cookie Policy",
    "/docs": "Docs",
    "/docs/shopify": "Shopify",
};

const normalisePath = (pathname: string): string => {
    if (!pathname || pathname === "/") return "/";
    const bare = pathname.split("?")[0].split("#")[0];
    return bare.length > 1 && bare.endsWith("/") ? bare.slice(0, -1) : bare;
};

const KNOWN_EXACT = new Set([
    "/",
    "/ai-chat-widget",
    "/ai-voice-agents",
    "/ai-knowledge-base",
    "/ai-workflow-builder",
    "/ai-call-pipeline",
    "/ai-outbound-triggers",
    "/ai-crm",
    "/sales-call-pipeline",
    "/managed-support",
    "/shopify-ai-customer-support",
    "/docs",
    "/docs/shopify",
    "/use-cases",
    "/case-studies",
    "/compare",
    "/alternatives",
    "/pricing",
    "/contact",
    "/privacy",
    "/terms",
    "/cookies",
    "/blogs",
]);

/** Routes that publish their own indexable page. Anything else is a miss. */
const isKnownPublicPath = (path: string): boolean => {
    if (KNOWN_EXACT.has(path)) return true;
    if (path.startsWith("/blog/")) return true;
    if (path.startsWith("/use-cases/")) return true;
    if (path.startsWith("/case-studies/")) return true;
    if (path.startsWith("/compare/")) return true;
    if (/^\/docs\/(get-started|manual)\/[^/]+$/.test(path)) return true;
    if (isPmsGuidePath(path)) return true;
    return false;
};

const isNoindexPath = (path: string): boolean =>
    // `/blogs` on the India build is an empty index until a post is published.
    (indiaBlogIndexNoindex() && path === "/blogs") ||
    // `/get-started/*` are paid-traffic signup forms. `/use-cases/:slug` is
    // the indexable page for the same vertical, so these must not compete.
    // Unknown URLs are noindex too: the shell must not look like a real page.
    !isKnownPublicPath(path) ||
    path === "/get-started" ||
    path.startsWith("/get-started/") ||
    path === "/login" ||
    path === "/signup" ||
    path.startsWith("/forgot-password") ||
    path.startsWith("/reset-password") ||
    path.startsWith("/accept-invitation") ||
    path.startsWith("/auth/") ||
    path.startsWith("/docs/print");

/** Pages that already emit a BreadcrumbList of their own. */
const hasOwnBreadcrumb = (path: string): boolean =>
    path === "/" ||
    path === "/use-cases" ||
    path.startsWith("/use-cases/") ||
    path === "/case-studies" ||
    path.startsWith("/case-studies/") ||
    path === "/compare" ||
    path.startsWith("/compare/") ||
    path === "/alternatives" ||
    path === "/blogs" ||
    path.startsWith("/blog/");

const humanize = (slug: string): string =>
    slug
        .split("-")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

type Crumb = { name: string; href: string };

const crumbsFor = (path: string): Crumb[] => {
    const home: Crumb = { name: "Home", href: "/" };
    const article = path.match(/^\/docs\/(get-started|manual)\/([^/]+)$/);
    if (article) {
        const tier: DocTier = article[1] === "get-started" ? "quickstart" : "manual";
        const section = getDocSection(article[2]);
        const name = section ? `${section.title} — ${TIER_META[tier].label}` : humanize(article[2]);
        return [home, { name: "Docs", href: "/docs" }, { name, href: path }];
    }
    if (path === "/docs/shopify") {
        return [home, { name: "Docs", href: "/docs" }, { name: "Shopify", href: path }];
    }
    if (LABELS[path]) {
        return [home, { name: LABELS[path], href: path }];
    }
    if (path.startsWith("/support/")) {
        return [home, { name: humanize(path.slice("/support/".length)), href: path }];
    }
    const leaf = path.split("/").filter(Boolean).pop() || "Page";
    return [home, { name: humanize(leaf), href: path }];
};

const breadcrumbSchema = (path: string) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbsFor(path).map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.name,
        item: `${marketingOrigin()}${crumb.href}`,
    })),
});

export default function PublicStructuredData() {
    const { pathname } = useLocation();
    const path = normalisePath(pathname);
    const noindex = isNoindexPath(path);

    useEffect(() => {
        if (!noindex) return;
        applyRobotsMeta(NOINDEX_ROBOTS);
    }, [noindex]);

    const organization = useMemo(
        () => (noindex || path === "/" || path === "/contact" ? null : buildOrganizationSchema()),
        [noindex, path],
    );
    const breadcrumb = useMemo(
        () => (noindex || hasOwnBreadcrumb(path) ? null : breadcrumbSchema(path)),
        [noindex, path],
    );

    if (!organization && !breadcrumb) return null;

    return (
        <>
            {organization ? <JsonLd id="site-organization" data={organization} /> : null}
            {breadcrumb ? <JsonLd id="site-breadcrumb" data={breadcrumb} /> : null}
        </>
    );
}
